import api from './api';
import { captureSession, assertCurrentSession } from '../utils/authSession';

export const shippingId = (value) => typeof value === 'string' && /^[a-f\d]{24}$/.test(value);
const fail = () => {
  throw new Error('Shipping documents unavailable.');
};
const scopeBody = ({ orderId, shipmentId, businessContext, distributorId }) => {
  if (!!orderId === !!shipmentId || !shippingId(orderId || shipmentId)) return fail();
  if (
    (businessContext !== undefined || distributorId !== undefined) &&
    (businessContext !== 'distributor' || !shippingId(distributorId))
  )
    return fail();
  return {
    ...(orderId ? { orderId } : { shipmentId }),
    ...(distributorId ? { businessContext, distributorId } : {}),
  };
};
const options = (input = {}, binary = false) => {
  const sessionBinding = input.sessionBinding || captureSession();
  if (!sessionBinding.userId || !sessionBinding.hadToken) fail();
  assertCurrentSession(sessionBinding);
  return {
    signal: input.signal,
    sessionBinding,
    timeout: binary ? 120000 : 20000,
    ...(input.skipAuthReplay ? { skipAuthReplay: true } : {}),
    ...(binary ? { responseType: 'blob' } : {}),
  };
};
async function post(path, body, input, binary = false) {
  const config = options(input, binary);
  const response = await api.post(path, body, config);
  assertCurrentSession(config.sessionBinding);
  return binary ? response : response?.data?.data;
}
function validDocument(row, admin = false) {
  if (
    !row ||
    !shippingId(row._id) ||
    !Number.isSafeInteger(row.byteLength) ||
    row.byteLength < 1 ||
    row.byteLength > 5 * 1024 * 1024 ||
    row.filename !== 'shipping-slip.pdf' ||
    typeof row.createdAt !== 'string' ||
    !Number.isFinite(Date.parse(row.createdAt))
  )
    return false;
  return admin
    ? Number.isSafeInteger(row.revision) &&
        row.revision >= 0 &&
        ['quarantined', 'ready', 'rejected'].includes(row.state) &&
        (!row.orderId || shippingId(row.orderId)) &&
        (!row.shipmentId || shippingId(row.shipmentId)) &&
        (row.orderNumber === undefined ||
          (typeof row.orderNumber === 'string' && row.orderNumber.length <= 100))
    : shippingId(row.shipmentId) &&
        row.kind === 'shipping_slip' &&
        row.contentType === 'application/pdf';
}

const shippingDocumentService = {
  async search(scope, input) {
    const body = scopeBody(scope);
    const data = await post('/orders/shipping-documents/search', body, input);
    if (
      !data ||
      !shippingId(data.orderId) ||
      (body.orderId && data.orderId !== body.orderId) ||
      (body.shipmentId && data.shipmentId !== body.shipmentId) ||
      !Array.isArray(data.documents) ||
      data.documents.some(
        (row) => !validDocument(row) || (body.shipmentId && row.shipmentId !== body.shipmentId)
      ) ||
      new Set(data.documents.map((row) => row._id)).size !== data.documents.length
    )
      fail();
    return data;
  },
  download(scope, documentId, input) {
    if (!shippingId(documentId)) fail();
    return post(
      '/orders/shipping-documents/download',
      { ...scopeBody(scope), documentId },
      input,
      true
    );
  },
  async reviewQueue({ before, state = 'quarantined' } = {}, input) {
    if (
      !['quarantined', 'ready', 'rejected'].includes(state) ||
      (before !== undefined && !shippingId(before))
    )
      fail();
    const data = await post(
      '/admin/shipping-documents/search',
      { state, limit: 20, ...(before ? { before } : {}) },
      input
    );
    if (
      !data ||
      !Array.isArray(data.documents) ||
      data.documents.length > 20 ||
      data.documents.some((row) => !validDocument(row, true) || row.state !== state) ||
      (data.nextBefore !== undefined && !shippingId(data.nextBefore))
    )
      fail();
    return data;
  },
  downloadForReview(row, input) {
    if (!validDocument(row, true)) fail();
    return post(
      '/admin/shipping-documents/download',
      { documentId: row._id, revision: row.revision },
      input,
      true
    );
  },
  async targets(orderNumber, input) {
    if (typeof orderNumber !== 'string' || !orderNumber.trim() || orderNumber.length > 100) fail();
    const containsControlCharacter = [...orderNumber].some((character) => {
      const code = character.charCodeAt(0);
      return code <= 31 || code === 127;
    });
    if (containsControlCharacter) fail();
    const data = await post(
      '/admin/shipping-documents/targets',
      { orderNumber: orderNumber.trim() },
      input
    );
    if (
      !data ||
      !Array.isArray(data.targets) ||
      data.targets.some(
        (row) =>
          !shippingId(row.orderId) ||
          !shippingId(row.shipmentId) ||
          row.orderNumber !== orderNumber.trim() ||
          typeof row.targetRevision !== 'string' ||
          !/^[a-f\d]{64}$/.test(row.targetRevision) ||
          typeof row.trackingNumber !== 'string' ||
          !row.trackingNumber ||
          row.trackingNumber.length > 100
      )
    )
      fail();
    return data.targets;
  },
  async decide(row, decision, input) {
    if (
      !validDocument(row, true) ||
      row.state === 'rejected' ||
      !['approve', 'reject'].includes(decision) ||
      (decision === 'approve' &&
        (!shippingId(row.orderId) ||
          !shippingId(row.shipmentId) ||
          !/^[a-f\d]{64}$/.test(row.targetRevision)))
    )
      fail();
    const data = await post(
      '/admin/shipping-documents/decision',
      {
        documentId: row._id,
        revision: row.revision,
        decision,
        ...(decision === 'approve'
          ? {
              orderId: row.orderId,
              shipmentId: row.shipmentId,
              targetRevision: row.targetRevision,
              confirmSingleOrder: true,
            }
          : {}),
      },
      { ...input, skipAuthReplay: true }
    );
    if (
      !validDocument(data, true) ||
      data._id !== row._id ||
      data.state !== (decision === 'approve' ? 'ready' : 'rejected') ||
      data.revision !== row.revision + 1 ||
      (decision === 'approve' &&
        (data.orderId !== row.orderId || data.shipmentId !== row.shipmentId))
    )
      fail();
    return data;
  },
};
export default shippingDocumentService;
